import { Alert, AlertDescription, AlertMedia, AlertTitle } from 'components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { MessageCircleQuestionMarkIcon } from 'lucide-react';
import posthog from 'posthog-js';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

/** « Que dire dans mon premier message ? »: what to write first, with examples. */
export function FirstMessageInspirationDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const exampleMessages = t<string, { returnObjects: true }, string[]>('chats.new.exampleMessages', {
    returnObjects: true,
  });

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle asChild>
            {/* leaves room for the close button */}
            <h3 className="pr-8">Que dire dans mon premier message ?</h3>
          </DialogTitle>
        </DialogHeader>
        {/* The advice is long: it scrolls inside a dialog that keeps clear of the screen's edges */}
        {/* Each paragraph, the quote and each list a block of its own, well apart */}
        <div className="max-h-[55dvh] space-y-4 overflow-y-auto text-base [&_li+li]:mt-4 [&_q]:block [&_ul]:list-disc [&_ul]:pl-5">
          <Trans
            i18nKey="chats.explainer.firstMessageModal"
            components={{
              strong: <strong />,
              p: <p />,
              ul: <ul />,
              li: <li />,
              quote: <q />,
              vspace: <div className="h-2" aria-hidden />,
            }}
          />
          <ul>{Array.isArray(exampleMessages) && exampleMessages.map(msg => <li key={msg}>{msg}</li>)}</ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * « Besoin d'inspiration ? », a card under the caregiver's intro opening the
 * dialog above (Figma « Info alert »).
 */
export default function FirstMessageInspiration() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Alert
        asChild
        variant="elevated"
        onClick={() => {
          posthog.capture('chats.first_message_inspiration_opened');
          setOpen(true);
        }}
      >
        <button type="button" className="cursor-pointer text-left">
          <AlertMedia>
            <MessageCircleQuestionMarkIcon aria-hidden />
          </AlertMedia>
          <AlertTitle>{t('messagingV3.conversation.inspiration')}</AlertTitle>
          <AlertDescription>{t('messagingV3.conversation.inspirationDescription')}</AlertDescription>
        </button>
      </Alert>
      <FirstMessageInspirationDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
